/**
 * 手抄自 C02 的 typed client（入口站版本）。
 *
 * 入口站是**完全公開**的（CANONICAL_NAMES §2），所以預設不送 Authorization header。
 * 只有在使用者選擇登入、且呼叫需要身分的端點時才帶 token。
 */

import type { ApiErrorBody, GameDetail, GamesResponse, LeaderboardRow, Paged } from './types';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly detail: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  getToken?: () => string | null;
  fetchImpl?: typeof fetch;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly getToken: () => string | null;
  private readonly fetchImpl: typeof fetch;

  constructor(opts: ApiClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/+$/, '');
    this.getToken = opts.getToken ?? (() => null);
    this.fetchImpl = opts.fetchImpl ?? ((...args) => globalThis.fetch(...args));
  }

  private async request<T>(path: string, opts: { signal?: AbortSignal; authenticated?: boolean } = {}): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (opts.authenticated) {
      const token = this.getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }

    let res: Response;
    try {
      res = await this.fetchImpl(`${this.baseUrl}${path}`, {
        headers,
        signal: opts.signal,
        credentials: 'omit',
        mode: 'cors',
      });
    } catch (cause) {
      throw new ApiError(0, 'NETWORK_ERROR', cause instanceof Error ? cause.message : String(cause));
    }

    const text = await res.text();
    let parsed: unknown = null;
    if (text) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
    }

    if (!res.ok) {
      const body = parsed as ApiErrorBody | null;
      throw new ApiError(
        res.status,
        body?.error?.code ?? `HTTP_${res.status}`,
        body?.error?.message ?? res.statusText,
        body?.error?.detail ?? {},
      );
    }
    return parsed as T;
  }

  /** C02 §2 `GET /games` — 目錄，含每個變體的線上人數。 */
  listGames(signal?: AbortSignal): Promise<GamesResponse> {
    return this.request<GamesResponse>('/games', { signal });
  }

  /** C02 §2 `GET /games/{game_key}` */
  getGame(gameKey: string, signal?: AbortSignal): Promise<GameDetail> {
    return this.request<GameDetail>(`/games/${encodeURIComponent(gameKey)}`, { signal });
  }

  /** C02 §2 `GET /games/{game_key}/leaderboard` — 匿名玩家只以 display_name 呈現。 */
  getLeaderboard(
    gameKey: string,
    params: { variant_key?: string; window?: '24h' | '7d' | 'all' } = {},
    signal?: AbortSignal,
  ): Promise<Paged<LeaderboardRow>> {
    const qs = new URLSearchParams();
    if (params.variant_key) qs.set('variant_key', params.variant_key);
    if (params.window) qs.set('window', params.window);
    const suffix = qs.toString() ? `?${qs}` : '';
    return this.request<Paged<LeaderboardRow>>(`/games/${encodeURIComponent(gameKey)}/leaderboard${suffix}`, { signal });
  }
}
